import { createContext, Fragment, useContext, useState, type ReactNode } from 'react';
import { setNumberLocale } from '../lib/format';
import { messages, type Messages } from './messages';

export const LANGS = [
  { code: 'en', label: 'EN', name: 'English' },
  { code: 'fr', label: 'FR', name: 'Français' },
  { code: 'pt', label: 'PT', name: 'Português' },
  { code: 'ar', label: 'AR', name: 'العربية' },
] as const;
export type Lang = (typeof LANGS)[number]['code'];

const STORAGE_KEY = 'upbeat.lang';
const isLang = (v: unknown): v is Lang => LANGS.some((l) => l.code === v);

/** `?lang=` (for shared links) > saved choice > browser language > English. */
function initialLang(): Lang {
  const fromUrl = new URLSearchParams(window.location.search).get('lang');
  if (isLang(fromUrl)) return fromUrl;
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (isLang(saved)) return saved;
  } catch { /* storage blocked */ }
  const browser = navigator.language.slice(0, 2);
  return isLang(browser) ? browser : 'en';
}

/**
 * Current strings. Read at render time; the provider remounts the tree on a language change,
 * so plain `t.key` reads stay current without every component subscribing to a context.
 */
export let t: Messages = messages.en;

function apply(lang: Lang) {
  t = messages[lang];
  setNumberLocale(lang);
  document.documentElement.lang = lang;
  document.documentElement.dir = lang === 'ar' ? 'rtl' : 'ltr';
}

const LangContext = createContext<{ lang: Lang; setLang: (l: Lang) => void }>({ lang: 'en', setLang: () => {} });
export const useLang = () => useContext(LangContext);

export function I18nProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<Lang>(() => {
    const l = initialLang();
    apply(l);
    return l;
  });
  const setLang = (l: Lang) => {
    try { localStorage.setItem(STORAGE_KEY, l); } catch { /* storage blocked */ }
    apply(l);
    setLangState(l);
  };
  // filters live in the URL and the query cache outside the tree, so a remount loses nothing
  return (
    <LangContext.Provider value={{ lang, setLang }}>
      <Fragment key={lang}>{children}</Fragment>
    </LangContext.Provider>
  );
}
